import { Link } from 'gatsby';
import { useTranslation } from 'react-i18next';
import React, { useMemo, useState, useEffect } from 'react'

import UTIcon from '../../components/icons';
import { useAuthContext } from '../../context/auth';
import useWindowSize from '../../hooks/useWindowSize';
import { useCourseContext } from '../../context/course';
import { usePartnerContext } from '../../context/partner';
import { PartnerColors } from '../../context/partner/types';

const DEFAULT_ICON_SIZE = 35;
const SIDEBAR_ICON_SIZE = 18;
const NAV_LINK_ICON_SIZE = 22;

const DEFAULT_LINKS = [{
    label: 'Classroom',
    path: '/',
    icon: 'classroom'
}, {
    label: 'My Account',
    path: '/my-account/',
    icon: 'user'
}, {
    label: 'User Guide',
    path: '/help/user-guide/',
    icon: 'question'

}, {
    label: 'My Messages',
    path: '/my-messages/',
    icon: 'message'
}]

const COURSE_TREE_TOGGLE = false;

export default function AppNavigation() {

    const { t } = useTranslation();
    const { colors } = usePartnerContext();
    const { course } = useCourseContext();

    const { isTablet } = useWindowSize();

    const { conversations } = useAuthContext();

    const [navLocations, setNavLocations] = useState<Record<string, any>>({});

    useEffect(() => {
        if (course) {
            const navLocationsUpdate = { ...navLocations }
            navLocationsUpdate[`/classroom/${course.slug}/`] = true;
            setNavLocations(navLocationsUpdate);
        }
    }, [course])

    // const activeCourse = !!navLocations[`/classroom/${course?.slug}/`];
    const activeCourse = COURSE_TREE_TOGGLE ? !!navLocations[`/classroom/${course?.slug}/`] : false;
    const courseDash = navLocations[`/classroom/${course?.slug}/`];

    const path = window?.location?.pathname;

    const accreditationImages = useMemo(() => {
        return course?.accreditationImages;
    }, [course?.accreditationImages])

    if (isTablet) return <></>;

    const unreadConversations = conversations?.filter((convo) => convo.fromUnread === true) || [];

    return (
        <>
            <aside className={"w-[220px] border-r"}
                style={{
                    backgroundColor: colors.white,
                    borderColor: colors.border || colors.gray200
                }}
            >
                {!activeCourse && (
                    <div className={"py-2"}>
                        {courseDash && (
                            <NavItem
                                to={`/classroom/${course?.slug}`}
                                icon={"course"}
                                label={t("dashboard")}
                                active={path === `/classroom/${course?.slug}/`}
                                colors={colors}
                            />
                        )}
                        {DEFAULT_LINKS.map(link => (
                            <NavItem
                                key={`NAV_LINK_${link.path}`}
                                to={link.path}
                                icon={link.icon}
                                label={link.label}
                                active={path === link.path}
                                colors={colors}
                                badge={link.label === 'My Messages' && unreadConversations.length > 0 ? unreadConversations.length : undefined}
                            />
                        ))}
                    </div>
                )}
                {activeCourse && <CourseNav course={course} navLocations={navLocations} setNavLocations={setNavLocations} />}
                {accreditationImages && (
                    <div className={"space-y-3"}>
                        {accreditationImages?.map((image) => (
                            <div key={`NAV_ACCRED_${image.id}`} className={"px-3 py-2"}>
                                <img src={image.url} alt={image.name} />
                            </div>
                        ))}
                    </div>
                )}
            </aside>
        </>
    )
}

// One sidebar link. The active item uses the partner's primary colour with
// their configured primaryText on top - that's the pair they chose to
// contrast, rather than a lightened primary with black text that may not.
type NavItemProps = {
    to: string;
    icon: string;
    label: string;
    active: boolean;
    colors: PartnerColors;
    badge?: number;
};

const NavItem = ({ to, icon, label, active, colors, badge }: NavItemProps) => {
    const fg = active ? colors.primaryText : colors.textColor;

    return (
        <Link
            to={to}
            aria-current={active ? "page" : undefined}
            className={`
                block px-4 py-2.5 mx-2 rounded-md
                transition-all duration-150
                ${active ? '' : 'hover:bg-gray-50'}
            `}
            style={{
                backgroundColor: active ? colors.primary : 'transparent',
            }}
        >
            <div className={"flex items-center space-x-3"}>
                <UTIcon
                    size={NAV_LINK_ICON_SIZE}
                    type={icon as any}
                    color={active ? colors.primaryText : colors.gray8}
                />
                <span
                    className={`text-sm flex-1 ${active ? 'font-semibold' : 'font-medium'}`}
                    style={{ color: fg }}
                >
                    {label}
                </span>
                {badge !== undefined && (
                    <div
                        style={active
                            ? { backgroundColor: colors.primaryText, color: colors.primary }
                            : { backgroundColor: colors.primary, color: colors.primaryText }}
                        className={"flex items-center justify-center font-semibold text-xs rounded-full min-w-[20px] h-[20px] px-1.5"}
                    >
                        {badge}
                    </div>
                )}
            </div>
        </Link>
    );
};

const CourseNav = ({ course, navLocations, setNavLocations }: { course: any, navLocations: Record<string, any>, setNavLocations: any }) => {

    const { colors } = usePartnerContext();

    return <>
        <div className={"px-5 py-3 block items-center border-b"}>
            <Link to={`/classroom/${course?.slug}`}><p className={"text-xl"}>{course?.name}</p></Link>
            <div className={"text-md"}>{course?.modules?.length || 0} Modules
            </div>
            {/* OF: REMOVED AS DIDN'T LOOK RIGHT */}
            {/* <div className='w-full'>
                <CourseProgress minified />
            </div> */}
        </div>
        {course?.modules?.map((module: any) => {
            const activeModule = !!navLocations[`/classroom/${course.slug}/${module.slug}/`];
            return <div key={module.id} className={"text-sm"}>
                <div className={"px-2 py-1 flex justify-between items-center border-b"}>
                    {/* HH: Render module */}
                    <Link to={`/classroom/${course.slug}/${module.slug}/`} className={"flex items-center"}>
                        <UTIcon type={"module"} size={SIDEBAR_ICON_SIZE} className={"mr-2"} color={document.location.pathname.match(`/classroom/${course.slug}/${module.slug}/`) ? colors.primary : colors.gray8} />
                        <span className={"w-full"}>{module?.shortName || module?.name}</span>
                    </Link>
                    <div className={"caret cursor-pointer"} onClick={() => {
                        if (!activeModule) {
                            const navLocationsUpdate = { ...navLocations }
                            navLocationsUpdate[`/classroom/${course.slug}/${module.slug}/`] = true;
                            setNavLocations(navLocationsUpdate);
                        } else {
                            const navLocationsUpdate = { ...navLocations }
                            delete navLocationsUpdate[`/classroom/${course.slug}/${module.slug}/`]
                            setNavLocations(navLocationsUpdate);
                        }
                    }}>
                        <UTIcon type={activeModule ? "minus" : "plus"} className={"mr-1"} size={SIDEBAR_ICON_SIZE} />
                    </div>
                </div>
                {/* HH: Render topics on module */}
                {activeModule && module?.topics?.map((topic: any) => {
                    const activeTopic = !!navLocations[`/classroom/${course.slug}/${module.slug}/${topic.slug}/`];
                    return <>
                        <div className={"px-2 py-1 flex justify-between items-center border-b ml-4"}>
                            <Link to={`/classroom/${course.slug}/${module.slug}/${topic.slug}/`} className={"flex items-center"}>
                                <UTIcon type={"topic"} size={SIDEBAR_ICON_SIZE} className={"mr-1"} color={document.location.pathname.match(`/classroom/${course.slug}/${module.slug}/${topic.slug}/`) ? colors.primary : colors.gray8} />
                                <span className={"w-full"}>  {topic?.name}</span>
                            </Link>
                            {!!topic?.subTopics?.length && <div className={"caret cursor-pointer"} onClick={() => {
                                if (!activeTopic) {
                                    const navLocationsUpdate = { ...navLocations }
                                    navLocationsUpdate[`/classroom/${course.slug}/${module.slug}/${topic.slug}/`] = true;
                                    setNavLocations(navLocationsUpdate);
                                } else {
                                    const navLocationsUpdate = { ...navLocations }
                                    delete navLocationsUpdate[`/classroom/${course.slug}/${module.slug}/${topic.slug}/`]
                                    setNavLocations(navLocationsUpdate);
                                }
                            }}>
                                <UTIcon type={activeTopic ? "minus" : "plus"} className={"mr-1"} size={SIDEBAR_ICON_SIZE} />
                            </div>}
                        </div>
                        {/* HH: Render subtopics on topic */}
                        {activeTopic && !!topic?.subTopics?.length && topic.subTopics.map((subTopic: any) => {
                            return <div key={subTopic.id} className={"px-2 py-1 flex items-center border-b ml-8"}>
                                <Link to={`/classroom/${course.slug}/${module.slug}/${topic.slug}/${subTopic.slug}`} className={"flex"}>
                                    <UTIcon type={"topic"} size={SIDEBAR_ICON_SIZE} className={"mr-2"} color={document.location.pathname.match(`/classroom/${course.slug}/${module.slug}/${topic.slug}/${subTopic.slug}`) ? colors.primary : colors.gray8} />
                                    <span className={"w-full"}>  {subTopic?.name}</span>
                                </Link>
                            </div>
                        })}
                        {/* HH: Render summary on parent topics */}
                        {activeTopic && !!topic?.subTopics?.length && <div className={"px-2 py-1 flex items-center border-b ml-8"}>
                            <Link to={`/classroom/${course.slug}/${module.slug}/${topic.slug}/summary`} className={"flex"}>
                                <UTIcon type={"topic"} size={SIDEBAR_ICON_SIZE} className={"mr-1"} color={document.location.pathname.match(`/classroom/${course.slug}/${module.slug}/${topic.slug}/summary`) ? colors.primary : colors.gray8} />
                                <span className={"w-full"}>Summary</span>
                            </Link>
                        </div>
                        }
                    </>
                })}
                {/* HH: Render module test */}
                {activeModule && <div className={"px-2 py-1 flex items-center border-b ml-4"}>
                    <Link to={`/classroom/${course.slug}/${module.slug}/module-test`} className={"flex"}>
                        <UTIcon type={"topic"} size={SIDEBAR_ICON_SIZE} className={"mr-1"} color={document.location.pathname.match(`/classroom/${course.slug}/${module.slug}/module-test`) ? colors.primary : colors.gray8} />
                        <span className={"w-full"}>Module Test</span>
                    </Link>
                </div>}
            </div>
        })}
    </>

};

